import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { 
  RefreshCw, CheckCircle, XCircle, AlertCircle, Clock,
  Package, Box, ShoppingCart, Truck, MapPin, Activity,
  TrendingUp, Zap, Timer, ClipboardList, PackageCheck,
  CircleDot, Navigation
} from 'lucide-react';

/**
 * ========================================
 * DOCUMENT FLOW TV DASHBOARD
 * ========================================
 * HIGH CONTRAST VERSION for TV Display
 */

const DocumentFlowTVDashboard = () => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('today');
  const [excludePending, setExcludePending] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    complete: 0,
    inProgress: 0,
    withIssues: 0
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollRef = useRef(null);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!autoScroll || !scrollRef.current) return;
    
    const scrollContainer = scrollRef.current;
    const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
    
    if (scrollHeight <= 0) return;

    const scrollInterval = setInterval(() => {
      scrollPositionRef.current += 1;
      
      if (scrollPositionRef.current >= scrollHeight) {
        setTimeout(() => {
          scrollPositionRef.current = 0;
          scrollContainer.scrollTop = 0;
        }, 3000);
      } else {
        scrollContainer.scrollTop = scrollPositionRef.current;
      }
    }, 50);

    return () => clearInterval(scrollInterval);
  }, [autoScroll, flows]);

  useEffect(() => {
    loadDocumentFlows();
    const interval = setInterval(loadDocumentFlows, 30000);
    return () => clearInterval(interval);
  }, [dateFilter, excludePending]);

  const loadDocumentFlows = async () => {
    // Calculate date range based on filter
    let startDate = null;
    const now = new Date();
    
    if (dateFilter === 'today') {
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (dateFilter === 'week') {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (dateFilter === 'month') {
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    // 'all' = no date filter

    let query = supabase
      .from('orders')
      .select(`
        id,
        order_number,
        status,
        created_at,
        total,
        user_id,
        box_allocation_id,
        delivery_address_id,
        picking_tasks (
          id,
          pick_number,
          status,
          started_at,
          completed_at,
          has_shortages
        ),
        packing_tasks (
          id,
          status,
          started_at,
          completed_at,
          has_discrepancies
        )
      `)
      .order('created_at', { ascending: false });

    // Apply date filter if set
    if (startDate) {
      query = query.gte('created_at', startDate.toISOString());
    }

    // Exclude pending orders if enabled
    if (excludePending) {
      query = query.neq('status', 'pending');
    }

    query = query.limit(200);

    const { data: orders, error } = await query;

    if (!error && orders) {
      const userIds = [...new Set(orders.map(o => o.user_id).filter(Boolean))];
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, name')
        .in('id', userIds);

      const profileMap = {};
      profiles?.forEach(p => { profileMap[p.id] = p; });

      const addressIds = [...new Set(orders.map(o => o.delivery_address_id).filter(Boolean))];
      const { data: addresses } = await supabase
        .from('addresses')
        .select('id, area, physical_address, complex_name, unit_number')
        .in('id', addressIds);

      const addressMap = {};
      addresses?.forEach(a => { addressMap[a.id] = a; });

      const orderIdsWithBoxes = orders.filter(o => o.box_allocation_id).map(o => o.box_allocation_id);
      let boxAllocationsMap = {};
      
      if (orderIdsWithBoxes.length > 0) {
        const { data: boxAllocations } = await supabase
          .from('box_allocations')
          .select(`id, order_id, allocated_at, delivery_boxes (box_number, current_zone)`)
          .in('id', orderIdsWithBoxes);

        boxAllocations?.forEach(ba => { boxAllocationsMap[ba.order_id] = ba; });
      }

      const orderIds = orders.map(o => o.id);
      const { data: deliveryAssignments } = await supabase
        .from('driver_assignments')
        .select('order_id, status, assigned_at, collected_at, loaded_at, gatepass_verified_at, delivered_at')
        .in('order_id', orderIds);

      const deliveryMap = {};
      deliveryAssignments?.forEach(da => { deliveryMap[da.order_id] = da; });

      const { data: statusHistory } = await supabase
        .from('order_status_history')
        .select('order_id, status, created_at')
        .in('order_id', orderIds)
        .in('status', ['delivered', 'out_for_delivery', 'ready_for_dispatch', 'packed', 'picking_complete', 'packing', 'picking']);

      const deliveredAtMap = {};
      const outForDeliveryAtMap = {};
      const readyForDispatchAtMap = {};
      const packedAtMap = {};
      const pickingCompleteAtMap = {};
      const packingAtMap = {};
      const pickingAtMap = {};
      
      statusHistory?.forEach(sh => { 
        if (sh.status === 'delivered') deliveredAtMap[sh.order_id] = sh.created_at;
        if (sh.status === 'out_for_delivery') outForDeliveryAtMap[sh.order_id] = sh.created_at;
        if (sh.status === 'ready_for_dispatch') readyForDispatchAtMap[sh.order_id] = sh.created_at;
        if (sh.status === 'packed') packedAtMap[sh.order_id] = sh.created_at;
        if (sh.status === 'picking_complete') pickingCompleteAtMap[sh.order_id] = sh.created_at;
        if (sh.status === 'packing') packingAtMap[sh.order_id] = sh.created_at;
        if (sh.status === 'picking') pickingAtMap[sh.order_id] = sh.created_at;
      });

      const documentFlows = orders.map(order => {
        const picking = order.picking_tasks?.[0];
        const packing = order.packing_tasks?.[0];
        const boxAllocation = boxAllocationsMap[order.id];
        const delivery = deliveryMap[order.id];
        const profile = profileMap[order.user_id];
        const address = addressMap[order.delivery_address_id];

        // Determine status for each phase - use order.status to help detect current active phase
        const orderStatus = order.status;
        
        // Picking phase
        const pickingStatus = picking?.status === 'completed' ? 'done' : 
                             (picking?.status === 'in_progress' || orderStatus === 'picking') ? 'active' : 
                             orderStatus === 'confirmed' ? 'active' : 'pending';
        
        // Packing phase - active if order is in packing or picking is complete
        const packingStatus = packing?.status === 'completed' ? 'done' :
                             (packing?.status === 'in_progress' || orderStatus === 'packing') ? 'active' : 
                             (orderStatus === 'picking_complete' && pickingStatus === 'done') ? 'active' : 'pending';
        
        // Box allocation - active if packing done but no box yet
        const hasBox = boxAllocation?.delivery_boxes?.box_number;
        const boxStatus = hasBox ? 'done' : 
                         (packingStatus === 'done' || orderStatus === 'packed') ? 'active' : 'pending';
        
        // Collection - active if box allocated and waiting for driver
        const collectedStatus = ['out_for_delivery', 'delivered'].includes(orderStatus) ? 'done' :
                               (hasBox && orderStatus === 'ready_for_dispatch') ? 'active' : 'pending';
        
        // Transit - active when driver is delivering
        const inTransitStatus = orderStatus === 'out_for_delivery' ? 'active' :
                               orderStatus === 'delivered' ? 'done' : 'pending';
        
        // Delivered
        const deliveredStatus = orderStatus === 'delivered' ? 'done' :
                               orderStatus === 'delivery_failed' ? 'failed' : 'pending';

        const hasIssues = picking?.has_shortages || packing?.has_discrepancies;
        const flowStatus = order.status === 'delivered' ? 'complete' :
                          ['confirmed', 'picking', 'packing', 'picking_complete', 'packed', 
                           'ready_for_dispatch', 'out_for_delivery'].includes(order.status) ? 'in_progress' : 'pending';

        // Get timestamps - prefer direct sources, fallback to status history
        // Picking timestamps
        const pickingStarted = picking?.started_at || pickingAtMap[order.id];
        const pickingCompleted = picking?.completed_at || pickingCompleteAtMap[order.id];
        
        // Packing timestamps
        const packingStarted = packing?.started_at || packingAtMap[order.id];
        const packingCompleted = packing?.completed_at || packedAtMap[order.id];
        
        // Box allocation time - use allocation record or packed status time
        const boxAllocatedTime = boxAllocation?.allocated_at || packedAtMap[order.id];
        
        // Collection time - prefer driver assignment data, fallback to out_for_delivery status
        const collectedAt = delivery?.gatepass_verified_at || delivery?.loaded_at || 
                           delivery?.collected_at || outForDeliveryAtMap[order.id];
        
        // Delivered time
        const deliveredTime = deliveredAtMap[order.id] || delivery?.delivered_at;

        return {
          id: order.id,
          orderNumber: order.order_number,
          customerName: profile?.name?.split(' ')[0] || 'Guest',
          zone: address?.area || boxAllocation?.delivery_boxes?.current_zone || '-',
          fullAddress: address ? `${address.physical_address || ''}${address.complex_name ? ', ' + address.complex_name : ''}${address.unit_number ? ' (' + address.unit_number + ')' : ''}` : null,
          boxNumber: boxAllocation?.delivery_boxes?.box_number || '-',
          total: order.total,
          orderTime: order.created_at,
          pickingStatus,
          pickingStarted: pickingStarted,
          pickingCompleted: pickingCompleted,
          packingStatus,
          packingStarted: packingStarted,
          packingCompleted: packingCompleted,
          boxStatus,
          boxAllocatedAt: boxAllocatedTime,
          collectedStatus,
          collectedAt: collectedAt,
          inTransitStatus,
          transitStartedAt: outForDeliveryAtMap[order.id],
          deliveredStatus,
          deliveredAt: deliveredTime,
          hasIssues,
          flowStatus
        };
      });

      setFlows(documentFlows);
      
      const complete = documentFlows.filter(f => f.flowStatus === 'complete').length;
      const inProgress = documentFlows.filter(f => f.flowStatus === 'in_progress').length;
      const withIssues = documentFlows.filter(f => f.hasIssues).length;
      
      setStats({
        total: documentFlows.length,
        complete,
        inProgress,
        withIssues
      });
    }
    
    setLoading(false);
  };

  const calculateDuration = (start, end) => {
    if (!start) return null;
    const startTime = new Date(start);
    const endTime = end ? new Date(end) : new Date();
    const diffMs = endTime - startTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return '<1m';
    if (diffMins < 60) return `${diffMins}m`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h${mins > 0 ? mins + 'm' : ''}`;
  };

  const formatTime = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', { 
      day: '2-digit', 
      month: 'short'
    });
  };

  const formatCurrency = (amount) => {
    return `R${parseFloat(amount || 0).toFixed(0)}`;
  };

  // Stage Node - HIGH CONTRAST with RED FLICKER for active
  const StageNode = ({ status, icon: Icon, completedAt, startedAt, transitDuration, isTransitStage, isDeliverStage }) => {
    const getStyles = () => {
      if (status === 'done') {
        return {
          bg: 'bg-green-500',
          border: 'border-green-300',
          iconColor: 'text-white',
          glow: 'shadow-lg shadow-green-500/60'
        };
      }
      if (status === 'active') {
        return {
          bg: 'bg-red-600',
          border: 'border-red-400',
          iconColor: 'text-white',
          glow: 'shadow-xl shadow-red-500/80 animate-flicker'
        };
      }
      if (status === 'failed') {
        return {
          bg: 'bg-red-500',
          border: 'border-red-300',
          iconColor: 'text-white',
          glow: 'shadow-lg shadow-red-500/60'
        };
      }
      return {
        bg: 'bg-gray-600',
        border: 'border-gray-500',
        iconColor: 'text-gray-300',
        glow: ''
      };
    };

    const styles = getStyles();
    const time = formatTime(completedAt);
    
    // Calculate duration based on stage type
    let duration;
    if (isTransitStage || isDeliverStage) {
      // For transit and deliver stages, use the pre-calculated transit duration
      duration = transitDuration;
    } else {
      // For other stages, calculate from start to completion
      duration = calculateDuration(startedAt, completedAt);
    }

    return (
      <div className="flex flex-col items-center min-w-[70px]">
        <style>{`
          @keyframes flicker {
            0%, 100% { opacity: 1; box-shadow: 0 0 20px 8px rgba(239, 68, 68, 0.8); }
            50% { opacity: 0.6; box-shadow: 0 0 10px 4px rgba(239, 68, 68, 0.4); }
          }
          .animate-flicker {
            animation: flicker 0.8s ease-in-out infinite;
          }
        `}</style>
        <div className={`w-9 h-9 rounded-lg ${styles.bg} ${styles.glow} border-2 ${styles.border} flex items-center justify-center transition-all duration-300`}>
          <Icon size={18} className={styles.iconColor} strokeWidth={2.5} />
        </div>
        <div className="mt-1.5 text-center">
          {status === 'done' && time ? (
            <>
              <div className="text-[11px] text-green-300 font-bold">{time}</div>
              {duration && <div className="text-[10px] text-gray-400">{duration}</div>}
            </>
          ) : status === 'active' ? (
            <div className="text-[11px] text-red-400 font-bold animate-pulse">
              {calculateDuration(startedAt, null) || '...'}
            </div>
          ) : (
            <div className="text-[11px] text-gray-500">—</div>
          )}
        </div>
      </div>
    );
  };

  // Pipeline Connector - THICKER & BRIGHTER
  const PipelineConnector = ({ fromStatus, toStatus }) => {
    const isActive = fromStatus === 'done';
    const isFlowing = fromStatus === 'done' && toStatus === 'active';
    
    return (
      <div className="flex-1 flex items-start pt-4">
        <div className={`w-full h-1.5 rounded-full transition-colors duration-300 ${
          isActive ? 'bg-green-500' : 'bg-gray-600'
        } ${isFlowing ? 'animate-pulse bg-gradient-to-r from-green-400 to-red-500' : ''}`} />
      </div>
    );
  };

  // Flow Pipeline
  const FlowPipeline = ({ flow }) => {
    // Calculate transit duration: time from collection to delivery
    const getTransitDuration = () => {
      if (!flow.collectedAt) return null;
      if (flow.deliveredStatus === 'done' && flow.deliveredAt) {
        // Order delivered - show final transit time (deliver - collect)
        return calculateDuration(flow.collectedAt, flow.deliveredAt);
      } else if (flow.inTransitStatus === 'active') {
        // Still in transit - elapsed time will be calculated live in StageNode
        return null;
      }
      return null;
    };

    // Calculate total order time: from order placed to delivered
    const getTotalOrderTime = () => {
      if (flow.deliveredStatus === 'done' && flow.deliveredAt && flow.orderTime) {
        return calculateDuration(flow.orderTime, flow.deliveredAt);
      }
      return null;
    };

    const transitDuration = getTransitDuration();
    const totalOrderTime = getTotalOrderTime();

    const stages = [
      { status: flow.pickingStatus, icon: ClipboardList, completedAt: flow.pickingCompleted, startedAt: flow.pickingStarted },
      { status: flow.packingStatus, icon: PackageCheck, completedAt: flow.packingCompleted, startedAt: flow.packingStarted },
      { status: flow.boxStatus, icon: Box, completedAt: flow.boxAllocatedAt, startedAt: flow.packingCompleted },
      { status: flow.collectedStatus, icon: Truck, completedAt: flow.collectedAt, startedAt: flow.boxAllocatedAt },
      // Transit: for completed orders show collect time + transit duration, for active show elapsed
      { 
        status: flow.inTransitStatus, 
        icon: Navigation, 
        completedAt: flow.collectedAt,    // Show when driver collected/started transit
        startedAt: flow.collectedAt,       // For elapsed calculation when active
        transitDuration: transitDuration,
        isTransitStage: true
      },
      // Deliver: shows delivered time, duration is total order time (placed to delivered)
      { 
        status: flow.deliveredStatus, 
        icon: MapPin, 
        completedAt: flow.deliveredAt,     // Show when delivered
        startedAt: null,                    // Not used
        transitDuration: totalOrderTime,    // Show total order time
        isDeliverStage: true
      },
    ];

    return (
      <div className="flex items-start w-full">
        {stages.map((stage, index) => (
          <React.Fragment key={index}>
            <StageNode 
              status={stage.status} 
              icon={stage.icon}
              completedAt={stage.completedAt}
              startedAt={stage.startedAt}
              transitDuration={stage.transitDuration}
              isTransitStage={stage.isTransitStage}
              isDeliverStage={stage.isDeliverStage}
            />
            {index < stages.length - 1 && (
              <PipelineConnector 
                fromStatus={stage.status}
                toStatus={stages[index + 1].status}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      {/* Header */}
      <header className="bg-[#1e293b] border-b-2 border-[#334155]">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-gradient-to-br from-green-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
                <Activity size={24} className="text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Order Flow Dashboard</h1>
                <p className="text-sm text-gray-400">Real-time order tracking</p>
              </div>
            </div>

            <div className="flex items-center gap-10">
              <div className="flex items-center gap-10">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">{stats.total}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">{stats.complete}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">Complete</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-400">{stats.inProgress}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">In Progress</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-400">{stats.withIssues}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">Issues</div>
                </div>
              </div>

              <div className="flex items-center gap-3 pl-8 border-l-2 border-[#334155]">
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="px-3 py-2 bg-[#334155] text-white text-sm font-medium rounded-lg border border-[#475569] focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer"
                >
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="all">All Time</option>
                </select>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={excludePending}
                    onChange={(e) => setExcludePending(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-500 text-amber-500 focus:ring-amber-500 bg-[#334155]"
                  />
                  <span className="text-sm text-gray-300">Exclude Pending</span>
                </label>
                <div className="text-right">
                  <div className="text-2xl font-mono font-bold text-white tracking-tight">
                    {currentTime.toLocaleTimeString('en-US', { hour12: false })}
                  </div>
                  <div className="text-xs text-gray-400">
                    {currentTime.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </div>
                </div>
                <button
                  onClick={() => setAutoScroll(!autoScroll)}
                  className={`p-2.5 rounded-lg transition-all ${
                    autoScroll 
                      ? 'bg-green-600 text-white shadow-lg shadow-green-600/30' 
                      : 'bg-gray-700 text-gray-400'
                  }`}
                  title={autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
                >
                  <TrendingUp size={18} strokeWidth={2.5} />
                </button>
                <button
                  onClick={loadDocumentFlows}
                  disabled={loading}
                  className="p-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-all shadow-lg shadow-cyan-600/30"
                >
                  <RefreshCw size={18} strokeWidth={2.5} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Column Headers */}
      <div className="bg-[#1e293b] border-b-2 border-[#334155] sticky top-0 z-10">
        <div className="px-6 py-2.5">
          <div className="grid grid-cols-12 gap-3 text-xs text-gray-300 uppercase tracking-wider font-bold">
            <div className="col-span-3">Order Details</div>
            <div className="col-span-8">
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-1.5 min-w-[70px] justify-center"><ClipboardList size={12} strokeWidth={2.5} /> Pick</span>
                <span className="flex items-center gap-1.5 min-w-[70px] justify-center"><PackageCheck size={12} strokeWidth={2.5} /> Pack</span>
                <span className="flex items-center gap-1.5 min-w-[70px] justify-center"><Box size={12} strokeWidth={2.5} /> Box</span>
                <span className="flex items-center gap-1.5 min-w-[70px] justify-center"><Truck size={12} strokeWidth={2.5} /> Collect</span>
                <span className="flex items-center gap-1.5 min-w-[70px] justify-center"><Navigation size={12} strokeWidth={2.5} /> Transit</span>
                <span className="flex items-center gap-1.5 min-w-[70px] justify-center"><MapPin size={12} strokeWidth={2.5} /> Deliver</span>
              </div>
            </div>
            <div className="col-span-1 text-center">Status</div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div 
        ref={scrollRef}
        className="overflow-y-auto"
        style={{ height: 'calc(100vh - 150px)' }}
        onMouseEnter={() => setAutoScroll(false)}
        onMouseLeave={() => setAutoScroll(true)}
      >
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
          </div>
        ) : flows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <Package size={48} className="mb-4 opacity-50" />
            <p className="text-lg">No orders found</p>
          </div>
        ) : (
          <div>
            {flows.map((flow, index) => (
              <div 
                key={flow.id}
                className={`px-6 py-2.5 border-b border-[#1e293b] transition-all duration-200 ${
                  flow.hasIssues ? 'bg-amber-900/30 border-l-4 border-l-amber-500' : ''
                } ${flow.flowStatus === 'complete' ? 'opacity-70' : ''}`}
                style={{ backgroundColor: index % 2 === 0 ? '#0f172a' : '#131c31' }}
              >
                <div className="grid grid-cols-12 gap-3 items-center">
                  {/* Order Details */}
                  <div className="col-span-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-mono font-bold text-sm ${
                        flow.flowStatus === 'complete' ? 'text-green-400' :
                        flow.hasIssues ? 'text-amber-400' : 'text-white'
                      }`}>
                        {flow.orderNumber}
                      </span>
                      <span className="text-gray-400 text-xs font-medium">
                        {formatDate(flow.orderTime)}
                      </span>
                      {flow.hasIssues && (
                        <AlertCircle size={14} className="text-amber-400" strokeWidth={2.5} />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-300">{flow.customerName}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-purple-400 font-semibold">{flow.zone}</span>
                      {flow.boxNumber !== '-' && (
                        <>
                          <span className="text-gray-500">•</span>
                          <span className="text-cyan-400 font-mono font-semibold">Box {flow.boxNumber}</span>
                        </>
                      )}
                    </div>
                    {flow.fullAddress && (
                      <div className="text-xs text-gray-400 mt-0.5 truncate" title={flow.fullAddress}>
                        {flow.fullAddress}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      <span>{formatTime(flow.orderTime)}</span>
                      <span>•</span>
                      <span className="text-white font-semibold">{formatCurrency(flow.total)}</span>
                    </div>
                  </div>

                  {/* Flow Pipeline */}
                  <div className="col-span-8">
                    <FlowPipeline flow={flow} />
                  </div>

                  {/* Status Badge */}
                  <div className="col-span-1 text-center">
                    {flow.flowStatus === 'complete' ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg shadow-lg shadow-green-600/30">
                        <CheckCircle size={12} strokeWidth={2.5} />
                        Delivered
                      </span>
                    ) : flow.hasIssues ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-xs font-bold rounded-lg shadow-lg shadow-amber-600/30">
                        <AlertCircle size={12} strokeWidth={2.5} />
                        Issue
                      </span>
                    ) : flow.flowStatus === 'in_progress' ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-bold rounded-lg shadow-lg shadow-red-600/30 animate-pulse">
                        <Zap size={12} strokeWidth={2.5} />
                        In Progress
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-600 text-white text-xs font-bold rounded-lg">
                        <Clock size={12} strokeWidth={2.5} />
                        Pending
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[#1e293b] border-t-2 border-[#334155] px-6 py-2">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-green-600 flex items-center justify-center shadow">
                <CheckCircle size={12} className="text-white" strokeWidth={2.5} />
              </div>
              <span className="text-gray-300 font-medium">Delivered</span>
            </span>
            <span className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-red-600 flex items-center justify-center shadow animate-pulse">
                <Zap size={12} className="text-white" strokeWidth={2.5} />
              </div>
              <span className="text-gray-300 font-medium">In Progress</span>
            </span>
            <span className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-gray-600 flex items-center justify-center">
                <Clock size={12} className="text-white" strokeWidth={2.5} />
              </div>
              <span className="text-gray-300 font-medium">Pending</span>
            </span>
            <span className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-amber-600 flex items-center justify-center shadow">
                <AlertCircle size={12} className="text-white" strokeWidth={2.5} />
              </div>
              <span className="text-gray-300 font-medium">Issue</span>
            </span>
          </div>
          <div className="flex items-center gap-4 text-gray-400">
            <span>Refresh: 30s</span>
            <span className="text-gray-600">•</span>
            <span>{flows.length} orders</span>
            <span className="text-gray-600">•</span>
            <span className="flex items-center gap-2 text-green-400 font-bold">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse shadow-lg shadow-green-500/50"></span>
              Live
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocumentFlowTVDashboard;
